import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";
import { getAssessmentStatus } from "@/lib/api";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Loader2, ArrowRight, AlertCircle } from "lucide-react";

const POLL_MS = 1500;

function StepIcon({ state }) {
  if (state === "done") return <CheckCircle2 className="h-4 w-4 text-[#6CC24A]" />;
  if (state === "running") return <Loader2 className="h-4 w-4 text-[#EAB308] animate-spin" />;
  if (state === "failed") return <AlertCircle className="h-4 w-4 text-[#EF4444]" />;
  return <Circle className="h-4 w-4 text-[#C7CBD1]" />;
}

export default function AssessmentProgress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const doc = await getAssessmentStatus(id);
        if (cancelled) return;
        setJob(doc);
        if (doc.status === "complete") {
          // Give the UI 800ms to show the last step tick, then redirect.
          setTimeout(() => navigate(`/dashboard/${id}`, { replace: true }), 900);
          return;
        }
        if (doc.status === "failed") {
          setError(doc.failure_reason || "The assessment failed. Please try again.");
          return;
        }
        timer.current = setTimeout(tick, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setError("Assessment not found.");
          return;
        }
        timer.current = setTimeout(tick, POLL_MS * 2);
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id, navigate]);

  const pct = job?.percent || 0;
  const canPeek = job?.overall_score !== null && job?.overall_score !== undefined;

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <NavBar />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
        <div className="mb-10">
          <div className="pp-label mb-2">Live Assessment</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-white">
            Analyzing every trust signal for
            <span className="block text-[#6CC24A] mt-1">
              {job?.business_name || "your business"}
            </span>
          </h1>
          <p className="mt-4 text-[#C7CBD1] max-w-2xl leading-relaxed">
            {job?.website && `${job.website}`} {job?.industry && ` · ${job.industry}`}
          </p>
        </div>

        {error && (
          <div
            className="pp-card p-6 border-l-2 border-[#EF4444]"
            data-testid="progress-error"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[#EF4444] mt-0.5" />
              <div className="flex-1">
                <div className="font-display text-white text-lg">
                  Something went wrong.
                </div>
                <div className="text-sm text-[#C7CBD1] mt-1">{error}</div>
                <button
                  onClick={() => navigate("/new")}
                  className="mt-4 pp-btn-primary flex items-center gap-2 text-sm"
                  data-testid="progress-retry"
                >
                  Try again <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {!error && (
          <div className="pp-card p-8" data-testid="progress-panel">
            {/* Big progress bar */}
            <div className="flex items-baseline justify-between mb-3">
              <div className="pp-label">Overall progress</div>
              <div className="metric text-2xl font-bold text-white">{pct}%</div>
            </div>
            <div className="h-2 bg-[#0F1115] border border-[#333C47] overflow-hidden">
              <motion.div
                className="h-full bg-[#6CC24A]"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              />
            </div>

            {/* Steps */}
            <ul className="mt-8 space-y-3" data-testid="progress-steps">
              {(job?.progress || []).map((step) => (
                <motion.li
                  key={step.id}
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 1 }}
                  className={`flex items-start gap-3 py-2 border-b border-[#333C47] last:border-0 ${
                    step.state === "done" ? "" : "text-[#C7CBD1]"
                  }`}
                  data-testid={`progress-step-${step.id}`}
                >
                  <div className="pt-0.5">
                    <StepIcon state={step.state} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-medium ${
                        step.state === "done"
                          ? "text-white"
                          : step.state === "running"
                            ? "text-white"
                            : "text-[#C7CBD1]"
                      }`}
                    >
                      {step.label}
                    </div>
                    {step.note && (
                      <div className="text-xs text-[#C7CBD1] mt-1 leading-relaxed">
                        {step.note}
                      </div>
                    )}
                  </div>
                </motion.li>
              ))}
            </ul>

            {/* Live score preview once score_calculated has completed */}
            {canPeek && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 pp-surface p-5 flex items-center justify-between gap-4 flex-wrap"
                data-testid="progress-live-score"
              >
                <div>
                  <div className="pp-label mb-1" style={{ color: "#6CC24A" }}>
                    Live preview
                  </div>
                  <div className="font-display text-white text-lg">
                    Trust Score is ready — recommendations still preparing.
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="pp-label text-[10px]">Score</div>
                    <div className="metric text-3xl font-bold text-white">
                      {job.overall_score}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/dashboard/${id}`)}
                    data-testid="progress-open-dashboard"
                    className="pp-btn-primary text-sm flex items-center gap-2"
                  >
                    Open dashboard <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        )}

        <p className="mt-8 text-xs text-[#C7CBD1] leading-relaxed max-w-2xl">
          You can leave this page and return later — the assessment continues on our
          servers. Trust Scores are calculated from deterministic rules; Claude then
          interprets the findings in plain English.
        </p>
      </div>
    </div>
  );
}
